import React, {useEffect, useState,  useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { API_URL } from '../../../constants/api';
import { decodeToken, getToken } from '../../../utils/TokenUtils';
import { exportElementToPDF } from '../../../utils/ktcUtils';
import { notification } from 'antd';
import { Link } from 'react-router-dom';

const TrialBalance = ({ report, value }) => {
  const [trialBalanceData, setTrialBalanceData] = useState([]);
  const [currentDate, setCurrentDate] = useState('');
  const reportRef = useRef(null);

  const token = getToken();
  const companyName = decodeToken(token, 'companyName');
  const companyBanner = decodeToken(token, 'banner');

  useEffect(() => {
    setTrialBalanceData(report || []);
    if (value?.to) {
      const formattedDate = new Intl.DateTimeFormat('en-GB').format(
        new Date(value.to)
      );
      setCurrentDate(formattedDate);
    }
  }, [report, value]);

  /* ---------------- HELPERS ---------------- */
  const parseAmount = (amount) =>
    typeof amount === 'string'
      ? parseFloat(amount.replace(/[^0-9.-]+/g, '')) || 0
      : amount || 0;

  const formatAmount = (amount) =>
    `$${amount?.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const totalDebit = trialBalanceData.reduce(
    (total, item) => total + parseAmount(item.DR),
    0
  );
  const totalCredit = trialBalanceData.reduce(
    (total, item) => total + parseAmount(item.CR),
    0
  );

  /* ---------------- EXPORT HELPERS ---------------- */
  const flattenForExport = () => {
    const rows = trialBalanceData.map((item) => ({
      Account: item.Account,
      Debit: parseAmount(item.DR),
      Credit: parseAmount(item.CR),
    }));
    rows.push({ Account: 'Total', Debit: totalDebit, Credit: totalCredit });
    return rows;
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(flattenForExport());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Trial Balance');
    XLSX.writeFile(wb, `Trial Balance as of ${value.to}.xlsx`);
  };

  const exportCSV = () => {
    const rows = flattenForExport();
    const csv =
      'Account,Debit,Credit\n' +
      rows.map((r) => `"${r.Account}",${r.Debit},${r.Credit}`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Trial Balance as of ${value.to}.csv`;
    link.click();
  };

  const exportPDF = async () => {
    try {
      await exportElementToPDF({
        element: reportRef.current,
        fileName: `Trial Balance as of ${value.to}.pdf`,
        orientation: 'p',
      });
    } catch (err) {
      notification.error({
        message: 'PDF Export Failed',
        description: err.message,
      });
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <button className="btn btn-success me-2" onClick={exportExcel}>
          Export Excel
        </button>
        <button className="btn btn-secondary me-2" onClick={exportCSV}>
          Export CSV
        </button>
        <button className="btn btn-danger" onClick={exportPDF}>
          Export PDF
        </button>
      </div>

      <div style={styles.container} ref={reportRef}>
        <div style={styles.headerSection}>
          <img
            src={`${API_URL}/${companyBanner}`}
            alt="Company Banner"
            style={styles.logo}
          />
          <h2>{companyName}</h2>
          <p style={{ fontWeight: 'bold' }}>Trial Balance as of {currentDate}</p>
        </div>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.header}>Account</th>
              <th style={styles.header}>Debit ($)</th>
              <th style={styles.header}>Credit ($)</th>
            </tr>
          </thead>
           <tbody>
            {trialBalanceData.map((item, index) => (
              <tr key={index}>
                <td style={styles.rowLabel}>{item.Account}</td>
                <td style={styles.rowAmount}>
                  {item.DR ? (
                    <a
                      href={`/form-report/transactions-list?account_type=${item.type}&main_account_id=${item.mainAccountId}&account_id=${item.accountId}&from=${value.from}&to=${value.to}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#007bff', textDecoration: 'none' }}
                    >
                      {formatAmount(parseAmount(item.DR))}
                    </a>
                  ) : (
                    ''
                  )}
                </td>
                <td style={styles.rowAmount}>
                  {item.CR ? (
                    <a
                      href={`/form-report/transactions-list?account_type=${item.type}&main_account_id=${item.mainAccountId}&account_id=${item.accountId}&from=${value.from}&to=${value.to}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#007bff', textDecoration: 'none' }}
                    >
                      {formatAmount(parseAmount(item.CR))}
                    </a>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
            ))}
            <tr>
              <td style={styles.totalLabel}>Total</td>
              <td style={styles.rowAmount}>
                <strong>{formatAmount(totalDebit)}</strong>
              </td>
              <td style={styles.rowAmount}>
                <strong>{formatAmount(totalCredit)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

/* ---------------- STYLES ---------------- */
const styles = {
  container: { width: '80%', margin: 'auto', fontFamily: 'Arial, sans-serif' },
  headerSection: { textAlign: 'center', marginBottom: 30 },
  logo: { width: '100%', height: 150, objectFit: 'cover', marginBottom: 10 },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: 20,
    boxShadow: '0 0 10px rgba(0,0,0,0.05)',
  },
  header: {
    padding: 12,
    textAlign: 'left',
    border: '1px solid #ccc',
    backgroundColor: '#f7f7f7',
    fontWeight: 'bold',
  },
  rowLabel: { padding: 10, border: '1px solid #ccc' },
  rowAmount: { padding: 10, textAlign: 'right', border: '1px solid #ccc' },
  totalLabel: {
    padding: 10,
    fontWeight: 'bold',
    border: '1px solid #333',
    backgroundColor: '#f0f0f0',
  },
};

export default TrialBalance;
